import React, { useState } from 'react';
import { Sheet, SheetContent } from '../../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { AlertCircle, Target, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type TestConfigDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  testConfig: any;
}

const TestConfigDialog = ({ isOpen, onClose, testConfig }: TestConfigDialogProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    targetAudience: {
      mobile: false,
      desktop: false,
    },
    productScope: 'single',
    productSelection: '',
    collectionSelection: '',
    prompt: '',
    aiInstructions: ''
  });

  const handleCreate = async () => {
    setLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLoading(false);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-full lg:max-w-2xl z-[100] p-0"
      >
        <div className="flex h-full flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">{t("optimizationTesting.configure_test")}: {testConfig?.name}</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Scrollable Content */}
          <ScrollArea className="flex-1 px-6">
            <div className="py-6 space-y-8">
              {/* Target Audience Section */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">{t("optimizationTesting.target_audience")}</Label>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("optimizationTesting.mobile_users")}</Label>
                      <div className="text-sm text-gray-500">
                        {t("optimizationTesting.mobile_users_description")}
                      </div>
                    </div>
                    <Switch
                      checked={config.targetAudience.mobile}
                      onCheckedChange={(checked) =>
                        setConfig(prev => ({
                          ...prev,
                          targetAudience: {
                            ...prev.targetAudience,
                            mobile: checked
                          }
                        }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>{t("optimizationTesting.desktop_users")}</Label>
                      <div className="text-sm text-gray-500">
                        {t("optimizationTesting.desktop_users_description")}
                      </div>
                    </div>
                    <Switch
                      checked={config.targetAudience.desktop}
                      onCheckedChange={(checked) =>
                        setConfig(prev => ({
                          ...prev,
                          targetAudience: {
                            ...prev.targetAudience,
                            desktop: checked
                          }
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Product Scope Section */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">{t("optimizationTesting.product_scope")}</Label>
                <Select 
                  value={config.productScope}
                  onValueChange={(value) => 
                    setConfig(prev => ({...prev, productScope: value}))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("optimizationTesting.select_scope")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">{t("optimizationTesting.single_product")}</SelectItem>
                    <SelectItem value="collection">{t("optimizationTesting.collection")}</SelectItem>
                    <SelectItem value="productType">{t("optimizationTesting.product_type")}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Dynamic Product Selection */}
                <div className="pt-2">
                  {config.productScope === 'single' && (
                    <Select onValueChange={(value) => 
                      setConfig(prev => ({...prev, productSelection: value}))
                    }>
                      <SelectTrigger>
                        <SelectValue placeholder={t("optimizationTesting.select_product")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product1">Running Shoes Pro</SelectItem>
                        <SelectItem value="product2">Ultra Comfort Runners</SelectItem>
                        <SelectItem value="product3">Trail Blazer X1</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {config.productScope === 'collection' && (
                    <Select onValueChange={(value) => 
                      setConfig(prev => ({...prev, collectionSelection: value}))
                    }>
                      <SelectTrigger>
                        <SelectValue placeholder={t("optimizationTesting.select_collection")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="summer">Summer Collection</SelectItem>
                        <SelectItem value="winter">Winter Essentials</SelectItem>
                        <SelectItem value="sport">Sport Performance</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {config.productScope === 'productType' && (
                    <Select onValueChange={(value) => 
                      setConfig(prev => ({...prev, productSelection: value}))
                    }>
                      <SelectTrigger>
                        <SelectValue placeholder={t("optimizationTesting.select_product_type")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shoes">Running Shoes</SelectItem>
                        <SelectItem value="apparel">Athletic Apparel</SelectItem>
                        <SelectItem value="accessories">Accessories</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* AI Instructions Section */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">{t("optimizationTesting.ai_test_generation")}</Label>
                <div className="space-y-2">
                  <Label>{t("optimizationTesting.current_content")}</Label>
                  <div className="p-3 bg-gray-50 rounded-md text-sm">
                    {testConfig?.examples[0]}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("optimizationTesting.generation_prompt")}</Label>
                  <Textarea 
                    placeholder={t("optimizationTesting.generation_prompt_placeholder")}
                    value={config.aiInstructions}
                    onChange={(e) => setConfig(prev => ({...prev, aiInstructions: e.target.value}))}
                    className="h-24"
                  />
                </div>
                <div className="bg-blue-50 p-4 rounded-md">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium">{t("optimizationTesting.prompt_tips")}</p>
                      <p>{t("optimizationTesting.prompt_tips_content")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          {/* Sticky Footer */}
          <div className="border-t bg-gray-50/90 backdrop-blur-sm p-6">
            <div className="flex justify-end space-x-4">
              <Button variant="outline" onClick={onClose}>{t("optimizationTesting.cancel")}</Button>
              <Button onClick={handleCreate} disabled={loading}>
                {loading ? t("optimizationTesting.creating_test") : t("optimizationTesting.create_launch_test")}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TestConfigDialog;